import { ToggleButton, makeStyles, mergeClasses, shorthands, tokens } from '@fluentui/react-components';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: tokens.spacingHorizontalS,
  },
  button: {
    justifyContent: 'center',
    minWidth: 0,
  },
  // Same selected treatment as the document toolbar's toggle buttons.
  buttonSelected: {
    ...shorthands.borderColor(tokens.colorNeutralStrokeAccessible),
  },
});

export type TToggleButtonGroupOption<TValue extends string> = {
  value: TValue;
  label: string;
};

type TProps<TValue extends string> = {
  options: readonly TToggleButtonGroupOption<TValue>[];
  value: TValue[];
  onChange: (value: TValue[]) => void;
  disabled?: boolean;
};

const ToggleButtonGroup = <TValue extends string>({ options, value, onChange, disabled }: TProps<TValue>) => {
  const classes = useStyles();

  const toggle = (optionValue: TValue) => {
    const isSelected = value.includes(optionValue);
    onChange(isSelected ? value.filter((selected) => selected !== optionValue) : [...value, optionValue]);
  };

  return (
    <div role="group" className={classes.root}>
      {options.map((option) => {
        const isSelected = value.includes(option.value);
        return (
          <ToggleButton
            key={option.value}
            type="button"
            appearance="secondary"
            size="small"
            checked={isSelected}
            disabled={disabled}
            onClick={() => toggle(option.value)}
            className={mergeClasses(classes.button, isSelected && classes.buttonSelected)}
          >
            {option.label}
          </ToggleButton>
        );
      })}
    </div>
  );
};

export { ToggleButtonGroup };
